import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type {
  MatchOverrideInput,
  MatchOverrideResponse,
  SuggestedMatchesResponse,
  UnmatchedRecipientsResponse,
} from "@busright/data-contract";

// =================================================================================================
// useMatch — hooks for the /api/match/* triad (Reconciliation surface only).
//
// These endpoints exist server-side but had no FE hook yet, so this file owns a small dedicated
// fetch wrapper rather than touching the shared api-client.ts/queries.ts (out of scope for this
// pass). Same conventions as the shared client: same-origin `/api/*`, credentials included so the
// Cloudflare Access cookie travels, and the response is pinned to the frozen contract shape.
//
// `retry: false` on the reads, matching the rest of the iteration-D hooks — a not-yet-live endpoint
// should surface as an error immediately, never spin or fabricate rows.
// =================================================================================================

const matchRequest = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(`/api/match${path}`, { credentials: "include", ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
};

const matchApi = {
  getUnmatched: (): Promise<UnmatchedRecipientsResponse> =>
    matchRequest<UnmatchedRecipientsResponse>("/unmatched"),
  getSuggestions: (recipientId: string): Promise<SuggestedMatchesResponse> =>
    matchRequest<SuggestedMatchesResponse>(
      `/suggest?recipientId=${encodeURIComponent(recipientId)}`,
    ),
  postOverride: (input: MatchOverrideInput): Promise<MatchOverrideResponse> =>
    matchRequest<MatchOverrideResponse>("/override", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
} as const;

export const matchQueryKeys = {
  unmatched: ["match", "unmatched"] as const,
  suggest: (recipientId: string) => ["match", "suggest", recipientId] as const,
};

// The unmatched-recipient backlog (the 217/278/495 "unmatched (DQ)" problem) — the list Reconciliation
// drives off of.
export const useUnmatchedRecipients = (): UseQueryResult<UnmatchedRecipientsResponse> =>
  useQuery({
    queryKey: matchQueryKeys.unmatched,
    queryFn: matchApi.getUnmatched,
    retry: false,
  });

// Suggested district/account candidates for one unmatched recipient, each carrying a matchMethod +
// confidence. Disabled until a recipient is actually selected so opening the list doesn't fan out a
// suggest call per row.
export const useSuggestedMatches = (
  recipientId: string | null,
): UseQueryResult<SuggestedMatchesResponse> =>
  useQuery({
    queryKey: matchQueryKeys.suggest(recipientId ?? ""),
    queryFn: () => matchApi.getSuggestions(recipientId ?? ""),
    enabled: recipientId !== null && recipientId.length > 0,
    retry: false,
  });

// Confirm/override a recipient → district mapping. In snapshot mode the endpoint echoes the input
// back as the resolved mapping (no real persistence) — the caller should show an optimistic success
// with a note, never claim a hard-persisted write it can't back up. Invalidates the unmatched list
// so a confirmed row drops off the backlog on the next read.
export const useMatchOverride = (): UseMutationResult<
  MatchOverrideResponse,
  Error,
  MatchOverrideInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: matchApi.postOverride,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchQueryKeys.unmatched });
    },
  });
};
